import { useEffect, useId, useRef, useState } from 'react';
import { Checkbox } from '../Checkbox/Checkbox';
import type { SelectOption, SelectSize } from './Select';

/**
 * Props of `<Select variant="multiple">` — a capped multi-select (#1653).
 * Rendered by the Select atom; not exported on its own.
 */
export interface SelectMultipleProps {
  variant: 'multiple';
  /** Visible label (required: the trigger and the option group are named by it). */
  label: string;
  options: readonly SelectOption[];
  /** Selected option values, in selection order. */
  values: readonly string[];
  onValuesChange: (values: string[]) => void;
  /**
   * At most this many options can be selected. Once reached, the other options
   * stay focusable but are aria-disabled and described by `hint`.
   */
  maxSelected?: number;
  /**
   * At least this many options stay selected: at the minimum, the selected
   * options are aria-disabled (described by `hint`) instead of unchecking.
   */
  minSelected?: number;
  /** Always-visible hint under the control, e.g. "Up to 3 parameters" (describes trigger and capped options). */
  hint?: string;
  /** Trigger text while nothing is selected. */
  placeholder?: string;
  size?: SelectSize;
  fullWidth?: boolean;
  id?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Disclosure button + checkbox group: the trigger (a native button, Enter /
 * Space) shows the selected labels in full — wrapping, never truncated — and
 * opens the option list in the page flow (no overlay, so nothing is clipped at
 * 320 px). Options are native checkboxes (Tab / Space). Escape closes the list
 * and returns focus to the trigger; so does clicking or tabbing outside.
 */
export function SelectMultiple({
  label,
  options,
  values,
  onValuesChange,
  maxSelected,
  minSelected,
  hint,
  placeholder,
  size = 'md',
  fullWidth = false,
  id,
  className,
  disabled = false,
}: SelectMultipleProps) {
  const generatedId = useId();
  const baseId = id ?? `select-multiple-${generatedId.replace(/:/g, '')}`;
  const labelId = `${baseId}-label`;
  const panelId = `${baseId}-options`;
  const hintId = `${baseId}-hint`;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selected = new Set(values);
  const limitReached = maxSelected !== undefined && values.length >= maxSelected;
  const atMinimum = minSelected !== undefined && values.length <= minSelected;
  const selectedLabels = options.filter((o) => selected.has(o.value)).map((o) => o.label);
  const summary = selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder ?? '';

  // While open: Escape (focus inside) closes and refocuses the trigger; a click
  // or focus outside the control closes it.
  useEffect(() => {
    const root = rootRef.current;
    if (!open || !root) return undefined;
    const onPointerDown = (event: MouseEvent): void => {
      if (!root.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && root.contains(document.activeElement)) {
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onFocusOut = (event: FocusEvent): void => {
      const next = event.relatedTarget as Node | null;
      // A click on an option's label moves focus to a focusable ancestor (e.g.
      // <main tabindex="-1">) first — that is not leaving the control.
      if (next && !root.contains(next) && !next.contains(root)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    root.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      root.removeEventListener('focusout', onFocusOut);
    };
  }, [open]);

  const toggle = (value: string, locked: boolean): void => {
    if (locked) return;
    if (selected.has(value)) onValuesChange(values.filter((v) => v !== value));
    else onValuesChange([...values, value]);
  };

  const wrapperClass = [
    'select-wrapper',
    'select-multiple',
    fullWidth ? 'select-wrapper--full-width' : '',
    disabled ? 'select-wrapper--disabled' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={rootRef} className={wrapperClass}>
      <span id={labelId} className="select-label">
        {label}
      </span>
      <div className="select-control-wrapper">
        <button
          ref={triggerRef}
          type="button"
          id={`${baseId}-trigger`}
          className={`select__field select__field--${size} select-multiple__trigger`}
          aria-expanded={open}
          aria-controls={panelId}
          aria-labelledby={`${labelId} ${baseId}-trigger`}
          aria-describedby={hint ? hintId : undefined}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="select-multiple__summary">{summary}</span>
        </button>
        <span className={`select-chevron${open ? ' select-chevron--open' : ''}`} aria-hidden="true">
          ▾
        </span>
      </div>
      {hint && (
        <span id={hintId} className="select-hint">
          {hint}
        </span>
      )}
      <fieldset id={panelId} className="select-multiple__panel" hidden={!open} aria-describedby={hint ? hintId : undefined}>
        <legend className="select-multiple__legend">{label}</legend>
        <ul className="select-multiple__options">
          {options.map((option, index) => {
            const isSelected = selected.has(option.value);
            // Over the maximum (unselected) or at the minimum (selected): locked, but still focusable.
            const capped = isSelected ? atMinimum : limitReached;
            const unavailable = option.disabled === true;
            return (
              <li key={option.value} className="select-multiple__option">
                <Checkbox
                  id={`${baseId}-option-${index}`}
                  label={option.label}
                  checked={isSelected}
                  disabled={unavailable && !isSelected}
                  aria-disabled={capped || undefined}
                  aria-describedby={capped && hint ? hintId : undefined}
                  className={capped ? 'select-multiple__checkbox--capped' : undefined}
                  onChange={() => toggle(option.value, capped || (unavailable && !isSelected))}
                />
              </li>
            );
          })}
        </ul>
      </fieldset>
    </div>
  );
}
